// ══════════════════════════════════════════════════════════════════════════════
// Category Registry, Single source of truth for all category logic
// ══════════════════════════════════════════════════════════════════════════════
//
// DOCTRINE:
//   The slug identifies a category. It does NOT contain business logic.
//   All logic resolves through the registry object.
//
//   BAD:  if (slug === 'bridal-boutiques') { ... }
//   GOOD: if (category.type === 'fashion') { ... }
//
// CONSUMERS:
//   - main.jsx            → CATEGORY_SLUGS, alias redirects
//   - geo.js              → VENDOR_CATEGORIES, GEO_TO_VENDOR_CAT
//   - RegionCategoryPage  → listing filter, page layout, CTA logic
//   - AdminDashboard      → DIRECTORY_CATEGORIES
//   - CategoryGrid        → homepage grid cards
//   - Aura                → department routing, mood, prompts
// ══════════════════════════════════════════════════════════════════════════════

// ── Types ─────────────────────────────────────────────────────────────────────
//
//   type:
//     'venue'    , physical wedding venues
//     'vendor'   , service providers (planners, photographers etc.)
//     'fashion'  , attire, boutiques, accessories (connects to Fashion Studio)
//     'editorial', future magazine-style categories
//
//   department:
//     'venues' | 'planning' | 'photography' | 'fashion' | 'beauty' |
//     'design' | 'food' | 'entertainment' | 'lifestyle'
//
//   pageMode:
//     'directory', standard listing grid
//     'editorial', Vogue-style, magazine treatment
//     'studio'   , connects to Fashion Studio (try-on CTA, boutique grid)
//
//   status:
//     'live'        , active, fully routed
//     'coming-soon' , page renders but shows coming-soon state
//     'hidden'      , not routed, admin-only

const REGISTRY = [

  // ── VENUES ─────────────────────────────────────────────────────────────────

  {
    slug:         'wedding-venues',
    label:        'Wedding Venues',
    labelShort:   'Venues',
    description:  'Curated luxury venues worldwide, country houses, castles, coastal estates and city settings.',
    type:         'venue',
    department:   'venues',
    status:       'live',

    canonicalUrl: '/wedding-venues',
    aliases:      [],

    listingTypes: ['wedding-venues', 'venue'],

    pageMode:     'directory',

    grid: { position: 0, active: true, image: null },

    hero: {
      title:    'Wedding Venues',
      subtitle: 'Country houses, castles, coastal estates and city settings. Every venue personally selected.',
      image:    null,
    },

    aura: {
      department: 'venues',
      mood:       ['grand', 'romantic', 'timeless'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'standard',
    },

    icon:      '🏛',
    sortOrder: 0,

    subCategories: [
      { slug: 'country-houses',  label: 'Country Houses' },
      { slug: 'castles-estates', label: 'Castles & Estates' },
      { slug: 'barn-venues',     label: 'Barn Venues' },
      { slug: 'hotel-venues',    label: 'Hotel Venues' },
      { slug: 'coastal-beach',   label: 'Coastal & Beach' },
      { slug: 'city-rooftop',    label: 'City & Rooftop' },
    ],
  },

  // ── PLANNING ───────────────────────────────────────────────────────────────

  {
    slug:         'wedding-planners',
    label:        'Wedding Planners',
    labelShort:   'Planners',
    description:  'Full-service and destination wedding planning studios.',
    type:         'vendor',
    department:   'planning',
    status:       'live',

    canonicalUrl: '/wedding-planners',
    aliases:      [],

    listingTypes: ['wedding-planners', 'planner'],

    pageMode:     'directory',

    grid: { position: 1, active: true, image: null },

    hero: {
      title:    'Wedding Planners',
      subtitle: 'The finest planning studios. Full-service, destination and day-of coordination.',
      image:    null,
    },

    aura: {
      department: 'planning',
      mood:       ['organised', 'luxurious', 'trusted'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'standard',
    },

    icon:      '📋',
    sortOrder: 1,

    subCategories: [
      { slug: 'full-planning',           label: 'Full Planning' },
      { slug: 'partial-planning',        label: 'Partial Planning' },
      { slug: 'day-coordination',        label: 'Day Coordination' },
      { slug: 'destination-specialists', label: 'Destination Specialists' },
    ],
  },

  // ── PHOTOGRAPHY ────────────────────────────────────────────────────────────

  {
    slug:         'photographers',
    label:        'Wedding Photographers',
    labelShort:   'Photography',
    description:  'Editorial, fine art and documentary wedding photographers.',
    type:         'vendor',
    department:   'photography',
    status:       'live',

    canonicalUrl: '/photographers',
    aliases:      [],

    listingTypes: ['photographers', 'photographer'],

    pageMode:     'directory',

    grid: { position: 2, active: true, image: null },

    hero: {
      title:    'Wedding Photographers',
      subtitle: 'Fine art, documentary and editorial photographers, imagery that lasts a lifetime.',
      image:    null,
    },

    aura: {
      department: 'photography',
      mood:       ['artistic', 'emotional', 'timeless'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'editorial',
    },

    icon:      '📸',
    sortOrder: 2,

    subCategories: [
      { slug: 'documentary', label: 'Documentary Style' },
      { slug: 'fine-art',    label: 'Fine Art' },
      { slug: 'editorial',   label: 'Editorial' },
      { slug: 'traditional', label: 'Traditional' },
    ],
  },

  {
    slug:         'videographers',
    label:        'Wedding Videographers',
    labelShort:   'Film',
    description:  'Cinematic wedding films and documentary videography.',
    type:         'vendor',
    department:   'photography',
    status:       'live',

    canonicalUrl: '/videographers',
    aliases:      [],

    listingTypes: ['videographers', 'videographer'],

    pageMode:     'directory',

    grid: { position: 3, active: true, image: null },

    hero: {
      title:    'Wedding Videographers',
      subtitle: 'Cinematic films that capture every moment. Documentary and fine art approaches.',
      image:    null,
    },

    aura: {
      department: 'photography',
      mood:       ['cinematic', 'romantic', 'emotional'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'standard',
    },

    icon:      '🎬',
    sortOrder: 3,

    subCategories: [
      { slug: 'cinematic',        label: 'Cinematic' },
      { slug: 'documentary-film', label: 'Documentary Film' },
      { slug: 'highlight-reels',  label: 'Highlight Reels' },
      { slug: 'drone-aerial',     label: 'Drone & Aerial' },
    ],
  },

  // ── FASHION ────────────────────────────────────────────────────────────────

  {
    slug:         'bridal-boutiques',
    label:        'Bridal Boutiques',
    labelShort:   'Boutiques',
    description:  'The finest bridal boutiques, multi-designer, independent and bespoke ateliers.',
    type:         'fashion',
    department:   'fashion',
    status:       'live',

    canonicalUrl: '/bridal-boutiques',
    aliases:      ['/wedding-vendors/bridal-boutiques', '/bridal-wear'],

    listingTypes: ['bridal-boutiques', 'boutique', 'bridal-boutique', 'bridal-wear', 'bridal-fashion'],

    pageMode:     'studio',

    grid: { position: 4, active: true, image: null },

    hero: {
      title:    'Bridal Boutiques',
      subtitle: 'The finest bridal boutiques, curated for the discerning bride. Try on gowns in our virtual studio.',
      image:    null,
    },

    aura: {
      department: 'fashion',
      mood:       ['romantic', 'luxurious', 'intimate'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'editorial',
    },

    icon:      '◆',
    sortOrder: 4,

    subCategories: [
      { slug: 'multi-designer',      label: 'Multi-Designer' },
      { slug: 'independent-boutique',label: 'Independent Boutique' },
      { slug: 'department-bridal',   label: 'Department & Flagship' },
      { slug: 'bespoke-boutique',    label: 'Bespoke & Made-to-Measure' },
    ],
  },

  {
    slug:         'bridal-dresses',
    label:        'Bridal Dresses',
    labelShort:   'Gowns',
    description:  'Designer gowns, couture and bespoke bridal fashion.',
    type:         'fashion',
    department:   'fashion',
    status:       'live',

    canonicalUrl: '/bridal-dresses',
    aliases:      [],

    listingTypes: ['bridal-dresses', 'bridal-dress', 'bridal-wear'],

    pageMode:     'studio',

    grid: { position: 5, active: true, image: null },

    hero: {
      title:    'Bridal Dresses',
      subtitle: 'Couture, designer and bespoke bridal gowns from the world\'s finest ateliers.',
      image:    null,
    },

    aura: {
      department: 'fashion',
      mood:       ['romantic', 'ethereal', 'luxurious'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'editorial',
    },

    icon:      '👗',
    sortOrder: 5,

    subCategories: [
      { slug: 'couture',  label: 'Couture' },
      { slug: 'designer', label: 'Designer' },
      { slug: 'vintage',  label: 'Vintage' },
      { slug: 'bespoke',  label: 'Bespoke' },
    ],
  },

  {
    slug:         'bridal-wear',
    label:        'Bridal Wear',
    labelShort:   'Bridal',
    description:  'Bridal fashion designers and ateliers.',
    type:         'fashion',
    department:   'fashion',
    status:       'archived',   // RETIRED, consolidated into 'bridal-boutiques'; /bridal-wear redirects there

    canonicalUrl: '/bridal-wear',
    aliases:      [],

    listingTypes: ['bridal-wear', 'bridal-fashion'],

    pageMode:     'studio',

    grid: { position: 6, active: false, image: null },

    hero: {
      title:    'Bridal Wear',
      subtitle: 'The world\'s finest bridal fashion, from heritage couture houses to emerging designers.',
      image:    null,
    },

    aura: {
      department: 'fashion',
      mood:       ['romantic', 'artistic', 'luxurious'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'editorial',
    },

    icon:      '👗',
    sortOrder: 6,

    subCategories: [],
  },

  {
    slug:         'guest-attire',
    label:        'Guest Attire',
    labelShort:   'Attire',
    description:  'Groom suits, groomswear, mother of the bride and guest fashion.',
    type:         'fashion',
    department:   'fashion',
    status:       'live',

    canonicalUrl: '/guest-attire',
    aliases:      [],

    listingTypes: ['guest-attire', 'menswear', 'groomswear'],

    pageMode:     'directory',

    grid: { position: 7, active: true, image: null },

    hero: {
      title:    'Guest Attire',
      subtitle: 'Groom and groomswear, mother of the bride and guest fashion from leading designers.',
      image:    null,
    },

    aura: {
      department: 'fashion',
      mood:       ['elegant', 'refined', 'classic'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'standard',
    },

    icon:      '👔',
    sortOrder: 7,

    subCategories: [
      { slug: 'groom-suits',    label: 'Groom & Groomsmen' },
      { slug: 'mother-of-bride',label: 'Mother of the Bride' },
      { slug: 'guest-fashion',  label: 'Guest Fashion' },
    ],
  },

  {
    slug:         'accessories',
    label:        'Wedding Accessories',
    labelShort:   'Accessories',
    description:  'Veils, headpieces, shoes and finishing touches.',
    type:         'fashion',
    department:   'fashion',
    status:       'live',

    canonicalUrl: '/accessories',
    aliases:      ['/wedding-accessories'],

    listingTypes: ['accessories', 'wedding-accessories'],

    pageMode:     'directory',

    grid: { position: 8, active: false, image: null },

    hero: {
      title:    'Wedding Accessories',
      subtitle: 'Veils, headpieces, shoes and the finishing touches that complete your look.',
      image:    null,
    },

    aura: {
      department: 'fashion',
      mood:       ['delicate', 'elegant', 'romantic'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'editorial',
    },

    icon:      '💎',
    sortOrder: 8,

    subCategories: [
      { slug: 'veils-headpieces', label: 'Veils & Headpieces' },
      { slug: 'shoes',            label: 'Shoes' },
      { slug: 'finishing-touches',label: 'Finishing Touches' },
    ],
  },

  {
    slug:         'jewellers',
    label:        'Wedding Jewellers',
    labelShort:   'Jewellery',
    description:  'Fine jewellery, engagement rings and wedding bands.',
    type:         'fashion',
    department:   'fashion',
    status:       'live',

    canonicalUrl: '/jewellers',
    aliases:      [],

    listingTypes: ['jewellers', 'jewellery', 'jewelry'],

    pageMode:     'editorial',

    grid: { position: 9, active: true, image: null },

    hero: {
      title:    'Wedding Jewellers',
      subtitle: 'Fine jewellery, engagement rings and wedding bands from the world\'s leading jewellers.',
      image:    null,
    },

    aura: {
      department: 'fashion',
      mood:       ['luxurious', 'timeless', 'precious'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'editorial',
    },

    icon:      '💍',
    sortOrder: 9,

    subCategories: [
      { slug: 'engagement-rings', label: 'Engagement Rings' },
      { slug: 'wedding-bands',    label: 'Wedding Bands' },
      { slug: 'fine-jewellery',   label: 'Fine Jewellery' },
    ],
  },

  // ── BEAUTY ─────────────────────────────────────────────────────────────────

  {
    slug:         'hair-makeup',
    label:        'Hair & Makeup',
    labelShort:   'Beauty',
    description:  'Bridal hair stylists and makeup artists.',
    type:         'vendor',
    department:   'beauty',
    status:       'live',

    canonicalUrl: '/hair-makeup',
    aliases:      [],

    listingTypes: ['hair-makeup', 'hair-and-makeup', 'makeup-artist', 'hair-stylist'],

    pageMode:     'directory',

    grid: { position: 10, active: true, image: null },

    hero: {
      title:    'Hair & Makeup',
      subtitle: 'Bridal hair stylists and makeup artists at the intersection of beauty and fashion.',
      image:    null,
    },

    aura: {
      department: 'beauty',
      mood:       ['glamorous', 'polished', 'radiant'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'standard',
    },

    icon:      '💄',
    sortOrder: 10,

    subCategories: [
      { slug: 'bridal-makeup',  label: 'Bridal Makeup' },
      { slug: 'bridal-hair',    label: 'Bridal Hair' },
      { slug: 'group-styling',  label: 'Group & Party Styling' },
    ],
  },

  {
    slug:         'health-beauty',
    label:        'Health & Beauty',
    labelShort:   'Wellness',
    description:  'Pre-wedding wellness, skincare and spa treatments.',
    type:         'vendor',
    department:   'beauty',
    status:       'live',

    canonicalUrl: '/health-beauty',
    aliases:      [],

    listingTypes: ['health-beauty', 'spa', 'wellness'],

    pageMode:     'directory',

    grid: { position: 11, active: false, image: null },

    hero: {
      title:    'Health & Beauty',
      subtitle: 'Pre-wedding wellness, skincare and spa treatments to prepare you for your day.',
      image:    null,
    },

    aura: {
      department: 'beauty',
      mood:       ['serene', 'radiant', 'balanced'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'minimal',
      cardStyle: 'standard',
    },

    icon:      '🌿',
    sortOrder: 11,

    subCategories: [
      { slug: 'skincare',        label: 'Skincare' },
      { slug: 'spa-wellness',    label: 'Spa & Wellness' },
      { slug: 'fitness-nutrition',label: 'Fitness & Nutrition' },
    ],
  },

  // ── DESIGN ─────────────────────────────────────────────────────────────────

  {
    slug:         'florists',
    label:        'Florists',
    labelShort:   'Florals',
    description:  'Luxury floral design, installations and bespoke arrangements.',
    type:         'vendor',
    department:   'design',
    status:       'live',

    canonicalUrl: '/florists',
    aliases:      [],

    listingTypes: ['florists', 'florist'],

    pageMode:     'directory',

    grid: { position: 12, active: true, image: null },

    hero: {
      title:    'Florists',
      subtitle: 'Luxury floral design and bespoke installations from the world\'s finest floral artists.',
      image:    null,
    },

    aura: {
      department: 'design',
      mood:       ['romantic', 'lush', 'artistic'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'editorial',
    },

    icon:      '💐',
    sortOrder: 12,

    subCategories: [
      { slug: 'luxury-floral-design', label: 'Luxury Floral Design' },
      { slug: 'sustainable-flowers',  label: 'Sustainable Flowers' },
      { slug: 'installation-artists', label: 'Installation Artists' },
    ],
  },

  {
    slug:         'styling-decor',
    label:        'Styling & Décor',
    labelShort:   'Décor',
    description:  'Event styling, tablescaping, set design and creative direction.',
    type:         'vendor',
    department:   'design',
    status:       'live',

    canonicalUrl: '/styling-decor',
    aliases:      [],

    listingTypes: ['styling-decor', 'styling', 'decor', 'event-stylist'],

    pageMode:     'directory',

    grid: { position: 13, active: true, image: null },

    hero: {
      title:    'Styling & Décor',
      subtitle: 'Tablescaping, set design, lighting and creative direction, every detail considered.',
      image:    null,
    },

    aura: {
      department: 'design',
      mood:       ['creative', 'elegant', 'atmospheric'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'standard',
    },

    icon:      '✨',
    sortOrder: 13,

    subCategories: [
      { slug: 'tablescaping',      label: 'Tablescaping' },
      { slug: 'set-design',        label: 'Set Design' },
      { slug: 'creative-direction',label: 'Creative Direction' },
      { slug: 'lighting-design',   label: 'Lighting Design' },
    ],
  },

  {
    slug:         'event-design',
    label:        'Event Design',
    labelShort:   'Design',
    description:  'Full-service event design and creative production.',
    type:         'vendor',
    department:   'design',
    status:       'live',

    canonicalUrl: '/event-design',
    aliases:      [],

    listingTypes: ['event-design', 'event-designer'],

    pageMode:     'directory',

    grid: { position: 14, active: false, image: null },

    hero: {
      title:    'Event Design',
      subtitle: 'Creative directors and event designers who transform spaces into extraordinary experiences.',
      image:    null,
    },

    aura: {
      department: 'design',
      mood:       ['creative', 'visionary', 'dramatic'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'standard',
    },

    icon:      '🎨',
    sortOrder: 14,

    subCategories: [],
  },

  {
    slug:         'event-production',
    label:        'Event Production',
    labelShort:   'Production',
    description:  'AV, staging, special effects and technical production.',
    type:         'vendor',
    department:   'design',
    status:       'live',

    canonicalUrl: '/event-production',
    aliases:      [],

    listingTypes: ['event-production', 'av-production'],

    pageMode:     'directory',

    grid: { position: 15, active: false, image: null },

    hero: {
      title:    'Event Production',
      subtitle: 'AV, staging, lighting rigs and special effects, the infrastructure behind extraordinary events.',
      image:    null,
    },

    aura: {
      department: 'design',
      mood:       ['dramatic', 'technical', 'spectacular'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'standard',
    },

    icon:      '🎪',
    sortOrder: 15,

    subCategories: [
      { slug: 'av-sound',        label: 'AV & Sound' },
      { slug: 'staging',         label: 'Staging' },
      { slug: 'special-effects', label: 'Special Effects' },
    ],
  },

  // ── FOOD ───────────────────────────────────────────────────────────────────

  {
    slug:         'caterers',
    label:        'Caterers',
    labelShort:   'Catering',
    description:  'Private dining, bespoke menus and luxury wedding catering.',
    type:         'vendor',
    department:   'food',
    status:       'live',

    canonicalUrl: '/caterers',
    aliases:      ['/catering'],

    listingTypes: ['caterers', 'caterer', 'catering'],

    pageMode:     'directory',

    grid: { position: 16, active: true, image: null },

    hero: {
      title:    'Caterers',
      subtitle: 'Private dining and bespoke menus from award-winning chefs and luxury caterers.',
      image:    null,
    },

    aura: {
      department: 'food',
      mood:       ['indulgent', 'refined', 'celebratory'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'standard',
    },

    icon:      '🍽',
    sortOrder: 16,

    subCategories: [
      { slug: 'fine-dining',    label: 'Fine Dining' },
      { slug: 'bespoke-menus',  label: 'Bespoke Menus' },
      { slug: 'street-food',    label: 'Street Food & Casual' },
      { slug: 'patisserie',     label: 'Patisserie & Dessert' },
    ],
  },

  {
    slug:         'wedding-cakes',
    label:        'Wedding Cakes',
    labelShort:   'Cakes',
    description:  'Bespoke cake design, sugar art and dessert tables.',
    type:         'vendor',
    department:   'food',
    status:       'live',

    canonicalUrl: '/wedding-cakes',
    aliases:      ['/cakes'],

    listingTypes: ['wedding-cakes', 'cake', 'cakes'],

    pageMode:     'directory',

    grid: { position: 17, active: true, image: null },

    hero: {
      title:    'Wedding Cakes',
      subtitle: 'Bespoke cake design and sugar artistry from the world\'s finest cake designers.',
      image:    null,
    },

    aura: {
      department: 'food',
      mood:       ['celebratory', 'artistic', 'indulgent'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'standard',
    },

    icon:      '🎂',
    sortOrder: 17,

    subCategories: [
      { slug: 'tiered-classic',    label: 'Tiered & Classic' },
      { slug: 'contemporary',      label: 'Contemporary Design' },
      { slug: 'sugar-art',         label: 'Sugar Art & Sculpture' },
      { slug: 'miniature-desserts',label: 'Miniature & Dessert Tables' },
    ],
  },

  // ── ENTERTAINMENT ──────────────────────────────────────────────────────────

  {
    slug:         'entertainment',
    label:        'Wedding Entertainment',
    labelShort:   'Music',
    description:  'Live music, DJs, string quartets and performance artists.',
    type:         'vendor',
    department:   'entertainment',
    status:       'live',

    canonicalUrl: '/entertainment',
    aliases:      [],

    listingTypes: ['entertainment', 'musician', 'live-band', 'dj'],

    pageMode:     'directory',

    grid: { position: 18, active: true, image: null },

    hero: {
      title:    'Wedding Entertainment',
      subtitle: 'Live bands, string quartets, DJs and performance artists to elevate your celebration.',
      image:    null,
    },

    aura: {
      department: 'entertainment',
      mood:       ['joyful', 'energetic', 'celebratory'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'standard',
    },

    icon:      '🎵',
    sortOrder: 18,

    subCategories: [
      { slug: 'live-bands',     label: 'Live Bands' },
      { slug: 'djs',            label: 'DJs' },
      { slug: 'string-quartets',label: 'String Quartets' },
    ],
  },

  {
    slug:         'celebrants',
    label:        'Celebrants',
    labelShort:   'Celebrants',
    description:  'Humanist, symbolic and non-religious ceremony celebrants.',
    type:         'vendor',
    department:   'entertainment',
    status:       'live',

    canonicalUrl: '/celebrants',
    aliases:      [],

    listingTypes: ['celebrants', 'celebrant'],

    pageMode:     'directory',

    grid: { position: 19, active: false, image: null },

    hero: {
      title:    'Celebrants',
      subtitle: 'Humanist and symbolic ceremony celebrants who craft deeply personal celebrations.',
      image:    null,
    },

    aura: {
      department: 'entertainment',
      mood:       ['personal', 'meaningful', 'joyful'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'minimal',
      cardStyle: 'standard',
    },

    icon:      '🎤',
    sortOrder: 19,

    subCategories: [
      { slug: 'humanist',  label: 'Humanist' },
      { slug: 'symbolic',  label: 'Symbolic' },
      { slug: 'religious', label: 'Religious' },
    ],
  },

  // ── LIFESTYLE ──────────────────────────────────────────────────────────────

  {
    slug:         'stationery',
    label:        'Stationery',
    labelShort:   'Stationery',
    description:  'Bespoke invitations, calligraphy and on-the-day stationery.',
    type:         'vendor',
    department:   'lifestyle',
    status:       'live',

    canonicalUrl: '/stationery',
    aliases:      [],

    listingTypes: ['stationery', 'invitation-designer'],

    pageMode:     'directory',

    grid: { position: 20, active: false, image: null },

    hero: {
      title:    'Stationery',
      subtitle: 'Bespoke invitations, calligraphy and on-the-day stationery from independent designers.',
      image:    null,
    },

    aura: {
      department: 'lifestyle',
      mood:       ['refined', 'personal', 'elegant'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'editorial',
      cardStyle: 'standard',
    },

    icon:      '✉',
    sortOrder: 20,

    subCategories: [
      { slug: 'luxury-invitations', label: 'Luxury Invitations' },
      { slug: 'calligraphy',        label: 'Calligraphy' },
      { slug: 'on-the-day',         label: 'On-the-Day Stationery' },
      { slug: 'digital-stationery', label: 'Digital & Hybrid' },
    ],
  },

  {
    slug:         'luxury-transport',
    label:        'Luxury Transport',
    labelShort:   'Transport',
    description:  'Classic cars, chauffeur services, helicopters and boats.',
    type:         'vendor',
    department:   'lifestyle',
    status:       'live',

    canonicalUrl: '/luxury-transport',
    aliases:      ['/transport'],

    listingTypes: ['luxury-transport', 'transport', 'chauffeur'],

    pageMode:     'directory',

    grid: { position: 21, active: false, image: null },

    hero: {
      title:    'Luxury Transport',
      subtitle: 'Classic cars, chauffeur services, helicopters and superyachts, arrive in style.',
      image:    null,
    },

    aura: {
      department: 'lifestyle',
      mood:       ['glamorous', 'grand', 'memorable'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'cinematic',
      cardStyle: 'standard',
    },

    icon:      '🚗',
    sortOrder: 21,

    subCategories: [
      { slug: 'classic-cars',  label: 'Classic Cars' },
      { slug: 'chauffeur',     label: 'Chauffeur Services' },
      { slug: 'helicopters',   label: 'Helicopters' },
      { slug: 'boats-yachts',  label: 'Boats & Yachts' },
    ],
  },

  {
    slug:         'gift-registry',
    label:        'Gift Registry',
    labelShort:   'Gifts',
    description:  'Curated gift registries, honeymoon funds and charitable giving.',
    type:         'vendor',
    department:   'lifestyle',
    status:       'live',

    canonicalUrl: '/gift-registry',
    aliases:      [],

    listingTypes: ['gift-registry', 'gift'],

    pageMode:     'directory',

    grid: { position: 22, active: false, image: null },

    hero: {
      title:    'Gift Registry',
      subtitle: 'Curated gift registries, honeymoon funds and charitable giving options.',
      image:    null,
    },

    aura: {
      department: 'lifestyle',
      mood:       ['thoughtful', 'personal', 'celebratory'],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'minimal',
      cardStyle: 'standard',
    },

    icon:      '🎁',
    sortOrder: 22,

    subCategories: [
      { slug: 'traditional-registry', label: 'Traditional Registry' },
      { slug: 'honeymoon-fund',        label: 'Honeymoon Fund' },
      { slug: 'charity-gifts',         label: 'Charitable Gifts' },
    ],
  },

  // ── UMBRELLA (not category pages, routing only) ───────────────────────────

  {
    slug:         'wedding-vendors',
    label:        'Wedding Vendors',
    labelShort:   'Vendors',
    description:  'All wedding vendors, the complete directory.',
    type:         'vendor',
    department:   null,
    status:       'live',

    canonicalUrl: '/wedding-vendors',
    aliases:      [],

    listingTypes: [],         // Special-cased in RegionCategoryPage

    pageMode:     'directory',

    grid: { position: -1, active: false, image: null },

    hero: {
      title:    'Wedding Vendors',
      subtitle: 'Every category of wedding vendor, curated, verified and ready to enquire.',
      image:    null,
    },

    aura: {
      department: null,
      mood:       [],
      prompts:    [],
    },

    theme: {
      accent:    'ivory-gold',
      heroStyle: 'minimal',
      cardStyle: 'standard',
    },

    icon:      '💼',
    sortOrder: -1,

    subCategories: [],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// Derived exports, everything consumers need, computed once
// ══════════════════════════════════════════════════════════════════════════════

/** All category slugs, replaces the CATEGORY_SLUGS array in main.jsx */
export const CATEGORY_SLUGS = REGISTRY.map(c => c.slug);

/** slug → full registry object */
const BY_SLUG = Object.fromEntries(REGISTRY.map(c => [c.slug, c]));

/** Look up a category by slug */
export function getCategoryBySlug(slug) {
  return BY_SLUG[slug] || null;
}

/**
 * Given any listing category value (canonical slug, alias, or listingType like
 * 'boutique', 'caterer', 'photographer'), return the canonical registry slug.
 * Falls back to the input if no match found.
 */
const LISTING_TYPE_TO_SLUG = (() => {
  const map = {};
  REGISTRY.forEach(c => {
    (c.listingTypes || []).forEach(t => { map[t] = c.slug; });
  });
  return map;
})();

export function getCanonicalCategorySlug(rawSlug) {
  if (!rawSlug) return rawSlug;
  // Direct slug match
  if (BY_SLUG[rawSlug]) return rawSlug;
  // listingType alias (e.g. 'boutique' → 'bridal-boutiques')
  if (LISTING_TYPE_TO_SLUG[rawSlug]) return LISTING_TYPE_TO_SLUG[rawSlug];
  // Path alias (e.g. '/catering' → '/caterers' → 'caterers')
  const canonicalPath = ALIAS_MAP[`/${rawSlug}`];
  if (canonicalPath) return canonicalPath.replace(/^\//, '');
  return rawSlug;
}

/** All aliases → canonical slug map (for redirect handling) */
export const ALIAS_MAP = Object.fromEntries(
  REGISTRY.flatMap(c =>
    (c.aliases || []).map(alias => [alias, c.canonicalUrl])
  )
);

/** All routable paths (canonical + aliases) */
export const ALL_ROUTABLE_PATHS = REGISTRY.flatMap(c => [
  c.canonicalUrl,
  ...(c.aliases || []),
]);

/** Replaces GEO_TO_VENDOR_CAT, slug → listingTypes[] */
export const SLUG_TO_LISTING_TYPES = Object.fromEntries(
  REGISTRY.map(c => [c.slug, c.listingTypes])
);

/** Replaces VENDOR_CATEGORIES in geo.js */
export const VENDOR_CATEGORIES_FROM_REGISTRY = REGISTRY
  .filter(c => c.department !== null)
  .map(c => ({ slug: c.slug, label: c.label, icon: c.icon }));

/** Homepage grid categories, ordered, active only */
export const GRID_CATEGORIES = REGISTRY
  .filter(c => c.grid.active)
  .sort((a, b) => a.grid.position - b.grid.position)
  .map(c => ({
    id:    c.slug,
    label: c.label,
    img:   c.grid.image,
    slug:  c.slug,
  }));

/** All departments with their categories */
export const DEPARTMENTS = [
  { id: 'venues',        label: 'Venues',       icon: '🏛' },
  { id: 'planning',      label: 'Planning',     icon: '📋' },
  { id: 'photography',   label: 'Photography',  icon: '📸' },
  { id: 'fashion',       label: 'Fashion',      icon: '◆'  },
  { id: 'beauty',        label: 'Beauty',       icon: '💄' },
  { id: 'design',        label: 'Design',       icon: '✨' },
  { id: 'food',          label: 'Food & Cake',  icon: '🍽' },
  { id: 'entertainment', label: 'Wedding Entertainment',icon: '🎵' },
  { id: 'lifestyle',     label: 'Lifestyle',    icon: '🎁' },
].map(dept => ({
  ...dept,
  categories: REGISTRY.filter(c => c.department === dept.id),
}));

/** Admin taxonomy tree, replaces DIRECTORY_CATEGORIES */
export const DIRECTORY_CATEGORIES_FROM_REGISTRY = REGISTRY
  .filter(c => c.department !== null)
  .sort((a, b) => a.sortOrder - b.sortOrder)
  .map(c => ({
    id:            c.slug,
    slug:          c.slug,
    name:          c.label,
    description:   c.description,
    count:         0,
    subCategories: (c.subCategories || []).map(s => ({
      id:    s.slug,
      slug:  s.slug,
      name:  s.label,
      count: 0,
    })),
  }));

export default REGISTRY;
